import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";

import {
  getEpoMailTokens,
  getEpoMailTokenSourceFields,
  saveEpoMailToken,
  setEpoMailTokenActive,
} from "../../../services/admin";

const get = (obj, camelKey, pascalKey, fallback = "") => {
  if (!obj) return fallback;

  if (Object.prototype.hasOwnProperty.call(obj, camelKey)) {
    return obj[camelKey];
  }

  if (Object.prototype.hasOwnProperty.call(obj, pascalKey)) {
    return obj[pascalKey];
  }

  return fallback;
};

const unwrapRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.Result)) return data.Result;

  return data && typeof data === "object" ? [data] : [];
};

const DATA_FROM_OPTIONS = [
  {
    value: "EPO",
    label: "EPO Details",
    category: "EPO",
    tokenPrefix: "Epo",
    description: "Vendor, amount, currency, purpose and other EPO fields.",
  },
  {
    value: "REQUESTER",
    label: "Requester / Creator",
    category: "REQUESTER",
    tokenPrefix: "Requester",
    description: "Information about the employee who created the EPO.",
  },
  {
    value: "APPROVER",
    label: "Current Approver",
    category: "APPROVER",
    tokenPrefix: "Approver",
    description: "Information about the employee currently approving the EPO.",
  },
  {
    value: "WORKFLOW",
    label: "Approval Workflow",
    category: "WORKFLOW",
    tokenPrefix: "Workflow",
    description: "Matched rule, current step, next step and approval policy.",
  },
  {
    value: "AUTHORITY",
    label: "Authority / Management",
    category: "AUTHORITY",
    tokenPrefix: "Authority",
    description: "Deputy, Director, President and configured authority details.",
  },
  {
    value: "ACTION",
    label: "Approval Actions",
    category: "ACTIONS",
    tokenPrefix: "Action",
    description: "System-generated approval and deny actions.",
  },
  {
    value: "SYSTEM",
    label: "System Information",
    category: "SYSTEM",
    tokenPrefix: "System",
    description: "Company name, current date and other system values.",
  },
];

const FORMAT_TYPES = ["TEXT", "HTML"];

const EMPTY_FORM = {
  id: null,
  tokenKey: "",
  displayName: "",
  description: "",
  category: "EPO",
  sourceType: "EPO",
  sourceKey: "",
  formatType: "TEXT",
  sortOrder: 100,
  isActive: true,
};

const normalizeTokenKey = (value) => {
  const name = String(value ?? "")
    .trim()
    .replace(/^\{\{/, "")
    .replace(/\}\}$/, "")
    .trim();

  return name ? `{{${name}}}` : "";
};

const toPascalCase = (value) =>
  String(value ?? "")
    .replace(/\{\{|\}\}/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

const getSourceOption = (sourceType) =>
  DATA_FROM_OPTIONS.find((item) => item.value === sourceType) ??
  DATA_FROM_OPTIONS[0];

const sourceFieldValue = (field) =>
  get(field, "sourceKey", "SourceKey", "");

const sourceFieldLabel = (field) =>
  get(field, "displayName", "DisplayName", sourceFieldValue(field));

const sourceFieldDataType = (field) =>
  get(field, "dataType", "DataType", "");

const buildAutomaticValues = (sourceType, field, sortOrder) => {
  const source = getSourceOption(sourceType);
  const sourceKey = sourceFieldValue(field);
  const fieldLabel = sourceFieldLabel(field);

  // Examples:
  // EPO + Vendor                       => {{EpoVendor}}
  // REQUESTER + EmailAddress           => {{RequesterEmailAddress}}
  // AUTHORITY + PRESIDENT.EmailAddress => {{AuthorityPresidentEmailAddress}}
  const tokenName = `${source.tokenPrefix}${toPascalCase(sourceKey)}`;

  const cleanFieldLabel = String(fieldLabel || sourceKey)
    .replace(/^EPO\s+/i, "")
    .replace(/^Requester\s+/i, "")
    .replace(/^Approver\s+/i, "")
    .replace(/^Workflow\s+/i, "")
    .trim();

  const displayName = cleanFieldLabel
    ? `${source.label.replace(" / Creator", "").replace(" / Management", "")} - ${cleanFieldLabel}`
    : source.label;

  const formatType =
    /html$/i.test(sourceKey) || /html/i.test(sourceFieldDataType(field))
      ? "HTML"
      : "TEXT";

  return {
    tokenKey: normalizeTokenKey(tokenName),
    displayName,
    description: `${displayName}. Source: ${sourceType}.${sourceKey}`,
    category: source.category,
    sourceType,
    sourceKey,
    formatType,
    sortOrder,
    isActive: true,
  };
};

const EpoMailTokenManager = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sourceFields, setSourceFields] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentSourceOption = useMemo(
    () => getSourceOption(form.sourceType),
    [form.sourceType],
  );

  const filteredSourceFields = useMemo(() => {
    const currentSourceType = String(form.sourceType || "")
      .trim()
      .toUpperCase();

    return sourceFields
      .filter((field) => {
        const sourceType = String(
          get(field, "sourceType", "SourceType", ""),
        )
          .trim()
          .toUpperCase();

        return sourceType === currentSourceType;
      })
      .sort((a, b) =>
        sourceFieldLabel(a).localeCompare(sourceFieldLabel(b), undefined, {
          sensitivity: "base",
        }),
      );
  }, [sourceFields, form.sourceType]);

  const selectedSourceField = useMemo(
    () =>
      filteredSourceFields.find(
        (field) => sourceFieldValue(field) === form.sourceKey,
      ) ?? null,
    [filteredSourceFields, form.sourceKey],
  );

  const duplicateToken = useMemo(() => {
    if (!form.sourceType || !form.sourceKey) return null;

    return (
      rows.find((row) => {
        const rowId = get(row, "id", "Id", null);
        const sameRow = form.id != null && String(rowId) === String(form.id);

        if (sameRow) return false;

        const rowSourceType = String(
          get(row, "sourceType", "SourceType", ""),
        )
          .trim()
          .toUpperCase();

        const rowSourceKey = String(
          get(row, "sourceKey", "SourceKey", ""),
        ).trim();

        return (
          rowSourceType === String(form.sourceType).toUpperCase() &&
          rowSourceKey === String(form.sourceKey).trim()
        );
      }) ?? null
    );
  }, [rows, form.id, form.sourceType, form.sourceKey]);

  const loadSourceFields = useCallback(async () => {
    try {
      const response = await getEpoMailTokenSourceFields();
      const data = response?.data;

      setSourceFields(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.result)
          ? data.result
          : Array.isArray(data?.Result)
          ? data.Result
          : [],
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Unable to load EPO source fields.",
      );
    }
  }, []);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getEpoMailTokens();

      const tokenRows = unwrapRows(response).sort(
        (a, b) =>
          Number(get(a, "sortOrder", "SortOrder", 100)) -
          Number(get(b, "sortOrder", "SortOrder", 100)),
      );

      setRows(tokenRows);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Unable to load EPO mail tokens.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
    loadSourceFields();
  }, [loadTokens, loadSourceFields]);

  const getNextSortOrder = () =>
    rows.reduce(
      (max, row) =>
        Math.max(max, Number(get(row, "sortOrder", "SortOrder", 0))),
      0,
    ) + 10;

  const openAdd = () => {
    setForm({
      ...EMPTY_FORM,
      sortOrder: getNextSortOrder(),
    });

    setAdvancedOpen(false);
    setError("");
    setSuccess("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: get(row, "id", "Id", null),
      tokenKey: get(row, "tokenKey", "TokenKey", ""),
      displayName: get(row, "displayName", "DisplayName", ""),
      description: get(row, "description", "Description", ""),
      category: get(row, "category", "Category", "EPO"),
      sourceType: get(row, "sourceType", "SourceType", "EPO"),
      sourceKey: get(row, "sourceKey", "SourceKey", ""),
      formatType: get(row, "formatType", "FormatType", "TEXT"),
      sortOrder: Number(get(row, "sortOrder", "SortOrder", 100)),
      isActive: Boolean(get(row, "isActive", "IsActive", true)),
    });

    setAdvancedOpen(false);
    setError("");
    setSuccess("");
    setDialogOpen(true);
  };

  const change = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSourceTypeChange = (event) => {
    const sourceType = event.target.value;
    const source = getSourceOption(sourceType);

    setForm((current) => ({
      ...current,
      sourceType,
      sourceKey: "",
      category: source.category,
      ...(current.id
        ? {}
        : {
            tokenKey: "",
            displayName: "",
            description: "",
            formatType: "TEXT",
          }),
    }));
  };

  const handleSourceFieldChange = (_event, field) => {
    if (!field) {
      setForm((current) => ({
        ...current,
        sourceKey: "",
        ...(current.id
          ? {}
          : {
              tokenKey: "",
              displayName: "",
              description: "",
              formatType: "TEXT",
            }),
      }));
      return;
    }

    setForm((current) => {
      const automatic = buildAutomaticValues(
        current.sourceType,
        field,
        current.sortOrder,
      );

      // On ADD: fill everything automatically.
      // On EDIT: preserve the existing token/display settings unless the admin
      // intentionally changes them under Advanced Settings.
      return current.id
        ? {
            ...current,
            sourceKey: automatic.sourceKey,
            category: automatic.category,
          }
        : {
            ...current,
            ...automatic,
            sortOrder: current.sortOrder,
          };
    });
  };

  const copyToken = async (tokenValue) => {
    const value = normalizeTokenKey(tokenValue);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setSuccess(`${value} copied.`);
    } catch {
      setError("Unable to copy token. Please copy it manually.");
    }
  };

  const save = async () => {
    setError("");
    setSuccess("");

    if (!form.sourceType) {
      setError("Please select where the data comes from.");
      return;
    }

    if (!String(form.sourceKey || "").trim()) {
      setError("Please select a field.");
      return;
    }

    const tokenKey = normalizeTokenKey(form.tokenKey);

    if (!tokenKey) {
      setError("Token could not be generated.");
      return;
    }

    if (!String(form.displayName || "").trim()) {
      setError("Display Name is required.");
      return;
    }

    if (duplicateToken) {
      setError(
        `This field is already available as ${get(
          duplicateToken,
          "tokenKey",
          "TokenKey",
          "",
        )}.`,
      );
      return;
    }

    setSaving(true);

    try {
      await saveEpoMailToken({
        ...form,
        tokenKey,
        displayName: String(form.displayName || "").trim(),
        description: String(form.description || "").trim(),
        category: String(form.category || "")
          .trim()
          .toUpperCase(),
        sourceType: String(form.sourceType || "")
          .trim()
          .toUpperCase(),
        sourceKey: String(form.sourceKey || "").trim(),
        formatType: String(form.formatType || "TEXT")
          .trim()
          .toUpperCase(),
        sortOrder: Number(form.sortOrder) || 100,
      });

      setDialogOpen(false);
      setSuccess(
        form.id
          ? "Mail token updated successfully."
          : `Mail token ${tokenKey} created successfully.`,
      );
      await loadTokens();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Unable to save EPO mail token.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const id = get(row, "id", "Id", null);
    const current = Boolean(get(row, "isActive", "IsActive", true));

    try {
      setError("");
      setSuccess("");

      await setEpoMailTokenActive(id, !current);

      setSuccess(
        `Token ${!current ? "activated" : "deactivated"} successfully.`,
      );

      await loadTokens();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          "Unable to update token status.",
      );
    }
  };

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}

      <Paper variant="outlined">
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              EPO Mail Fields
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Choose where the data comes from and select a field. The token is
              created automatically.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadTokens}
              disabled={loading}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
            >
              Add Mail Field
            </Button>
          </Stack>
        </Box>

        <TableContainer
          sx={{
            maxHeight: 420,
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mail Field</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Data From</TableCell>
                <TableCell>Source Field</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No EPO mail fields found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const id = get(row, "id", "Id", null);
                  const active = Boolean(
                    get(row, "isActive", "IsActive", true),
                  );
                  const sourceType = get(
                    row,
                    "sourceType",
                    "SourceType",
                    "EPO",
                  );
                  const source = getSourceOption(sourceType);
                  const token = get(row, "tokenKey", "TokenKey", "");

                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {get(row, "displayName", "DisplayName", "")}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontWeight={600}
                          >
                            {token}
                          </Typography>

                          <Tooltip title="Copy token">
                            <IconButton
                              size="small"
                              onClick={() => copyToken(token)}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip size="small" label={source.label} />
                      </TableCell>

                      <TableCell>
                        {get(row, "sourceKey", "SourceKey", "")}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          color={active ? "success" : "default"}
                          label={active ? "Active" : "Inactive"}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => openEdit(row)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={active ? "Deactivate" : "Activate"}>
                          <IconButton onClick={() => toggleActive(row)}>
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={saving ? undefined : () => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.id ? "Edit EPO Mail Field" : "Add EPO Mail Field"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <Box>
              <Typography variant="body2" fontWeight={700} gutterBottom>
                1. Data From
              </Typography>

              <TextField
                select
                fullWidth
                value={form.sourceType}
                onChange={handleSourceTypeChange}
              >
                {DATA_FROM_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                {currentSourceOption.description}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={700} gutterBottom>
                2. Field
              </Typography>

              <Autocomplete
                fullWidth
                options={filteredSourceFields}
                value={selectedSourceField}
                onChange={handleSourceFieldChange}
                getOptionLabel={(option) => sourceFieldLabel(option)}
                isOptionEqualToValue={(option, value) =>
                  sourceFieldValue(option) === sourceFieldValue(value)
                }
                renderOption={(props, option) => (
                  <li {...props} key={sourceFieldValue(option)}>
                    <Box>
                      <Typography variant="body2">
                        {sourceFieldLabel(option)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sourceFieldValue(option)}
                        {sourceFieldDataType(option)
                          ? ` • ${sourceFieldDataType(option)}`
                          : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search field, e.g. email, department, vendor..."
                    helperText={`${filteredSourceFields.length} field(s) available`}
                  />
                )}
              />
            </Box>

            {form.sourceKey ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "background.default",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  TOKEN PREVIEW
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ mt: 0.75 }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {form.displayName}
                    </Typography>

                    <Typography
                      variant="body1"
                      fontFamily="monospace"
                      fontWeight={700}
                    >
                      {normalizeTokenKey(form.tokenKey)}
                    </Typography>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyToken(form.tokenKey)}
                  >
                    Copy Token
                  </Button>
                </Stack>

                {duplicateToken ? (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    This field already exists as{" "}
                    <strong>
                      {get(duplicateToken, "tokenKey", "TokenKey", "")}
                    </strong>
                    . You can use the existing token instead.
                  </Alert>
                ) : null}
              </Paper>
            ) : null}

            <Divider />

            <Button
              size="small"
              onClick={() => setAdvancedOpen((value) => !value)}
              endIcon={
                advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{ alignSelf: "flex-start" }}
            >
              Advanced Settings
            </Button>

            <Collapse in={advancedOpen}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Token Name"
                    value={form.tokenKey}
                    onChange={(e) => change("tokenKey", e.target.value)}
                    helperText="Normally generated automatically."
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Display Name"
                    value={form.displayName}
                    onChange={(e) => change("displayName", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={form.description}
                    onChange={(e) => change("description", e.target.value)}
                    multiline
                    minRows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Format Type"
                    value={form.formatType}
                    onChange={(e) => change("formatType", e.target.value)}
                  >
                    {FORMAT_TYPES.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Sort Order"
                    value={form.sortOrder}
                    onChange={(e) =>
                      change("sortOrder", Number(e.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isActive}
                        onChange={(e) =>
                          change("isActive", e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={save}
            disabled={saving || !form.sourceKey || Boolean(duplicateToken)}
          >
            {form.id ? "Update Mail Field" : "Add Mail Field"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EpoMailTokenManager;
